/* eslint-disable react/jsx-key */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getResetPasswordError, GetResetPasswordErrors, InvalidUsernameOrPasswordError } from '../../common/functions/getApiErrorMessage';
import translate from '../../common/functions/translate';
import {useForgotPasswordApi} from '../../common/hooks/useForgotPasswordApi';
import { DateObject } from '../../common/objects/models/date.field.value';
import ForgotPassword from '../../forgot-password/components/ForgotPassword';
import ForgotUsername from '../../forgot-username/components/ForgotUsername';

interface ForgotUsernameAndPasswordProps {
    testStep: number,
    testSecretQuestion?: string
}

type ForgotUsernameAndPasswordErrorKeys = InvalidUsernameOrPasswordError | 'couldNotProcess' | 'noLongerActive' | 'lockedToProtect' | 'mustLogIn' | 'callToGetTempPassword' | 'cannotBeFound' | 'invalidMBI' | 'invalidLastName' | 'invalidDOB' | 'invalidUsername' | 'didNotMatch' | 'tempUsername';

export default function ForgotUsernameAndPassword(props: ForgotUsernameAndPasswordProps) {
    const [step, setStep] = useState(props.testStep ? props.testStep : 0);
    const [forgotUsernameData, setForgotUsernameData] = useState<{
        medicareNumber: string
        suffix: string
        lastName: string
        username: string
        dateOfBirth: DateObject
    }>();
    const [secretQuestion, setSecretQuestion] = useState(props.testSecretQuestion || '');
    const [error, setError] = useState<JSX.Element | string | null>('');
    const [isLoading, setIsLoading] = useState(false);
    const forgotPasswordApi = useForgotPasswordApi();
    const [forgotUsernameError, setForgotUsernameError] = useState<ForgotUsernameAndPasswordErrorKeys | null>(null);

    useEffect(() => {
        const errorStateToMessage = {
            noLongerActive: <>
                <span className='ds-u-font-weight--bold'>{translate('your_account_not_active')}</span>
                <p>
                    {translate('to_reactivate_account_call_medicare')} ({<a href="tel:18006334227">1-800-633-4227</a>}).
                    {' '}{translate('tty_users_can_call')} {<a href="tel:1-877-486-2048">1-877-486-2048</a>}.
                </p>
            </>,
            lockedToProtect: <>
                {translate('your_account_lock_protect_information')}
                {' '}<Link to="/unlock-account">{translate('unlock_my_account')}.</Link>
            </>,
            mustLogIn: <>
                {translate('you_must')}
                {' '}<Link to="/login">{translate('log_in')}</Link>
                {' '}{translate('with_temporary_password')}
            </>,
            callToGetTempPassword: translate("you_have_not_setup_secret_question"),
            isAlreadyUsed: translate(`you_entered_password_used_before`),
            pleaseEnterUsername: translate('please_enter_username_meet_guidelines'),
            passwordGuidelines: translate('the_password_does_not_meet_required_enter_correct_info'),
            cannotBeFound: translate("we_cant_find_you_check_info"),
            couldNotProcess: translate("we_cant_process_request_try_again"),
            // This is here to (temporarily) satisfy typing requirement of shared function 'getResetPasswordError'.
            invalidMBI: '',
            invalidLastName: '',
            invalidDOB: '',
            invalidUsername: '',
            didNotMatch: '',
            tempUsername: ''
        }
        setError(forgotUsernameError ? errorStateToMessage[forgotUsernameError] : '');
    }, [setError, forgotUsernameError]);

    const next = () => setStep(current => current + 1);
    const prev = () => setStep(current => current - 1);

    const getSecretQuestion = async () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);

        const { medicareNumber, lastName, suffix, username, dateOfBirth } = forgotUsernameData!;

        try {
            const question = await forgotPasswordApi.getSecretQuestion({
                mbi: medicareNumber,
                username,
                lastName,
                suffix,
                dateOfBirth
            });
            setError('');
            setSecretQuestion(question);
            next();
        } catch (e: any) {
            let errorMessage: InvalidUsernameOrPasswordError | GetResetPasswordErrors;
            if (e.code) {
                errorMessage = getResetPasswordError(e.code, e.errors)['applicationErrorMessage'];
            } else {
                errorMessage = `couldNotProcess`;
            }
            setForgotUsernameError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const steps = [
        <ForgotUsername
            titleText={translate('forgot_username_and_password')}
            subTitle={translate('get_username_and_reset_password')}
            buttonText={translate('next')}
            combinedWithPassword
            onData={setForgotUsernameData}
            initData={forgotUsernameData}
            onNextClick={getSecretQuestion}
            onNextLoading={isLoading}
            getSecretQuestionErrorMessage={error}
        />,
        <ForgotPassword
            combinedWithUsername
            secretQuestion={secretQuestion}
            onBackClick={prev}
            forgotUsernameData={forgotUsernameData}
        />
    ]

    return steps[step];
}
